import React from 'react';
import { TriageLevel, LanguageCode } from '../types';
import { TRANSLATIONS } from '../constants';
import { AlertTriangle, AlertCircle, Eye, Heart } from 'lucide-react';

interface TriageBadgeProps {
  level: TriageLevel;
  language: LanguageCode;
}

const TRIAGE_STYLES: Record<
  NonNullable<TriageLevel>,
  { bg: string; border: string; text: string; icon: React.FC<any>; pulse: boolean }
> = {
  emergency: {
    bg: 'bg-red-950/60',
    border: 'border-red-600/60',
    text: 'text-red-300',
    icon: AlertTriangle,
    pulse: true,
  },
  urgent: {
    bg: 'bg-orange-950/60',
    border: 'border-orange-600/50',
    text: 'text-orange-300',
    icon: AlertCircle,
    pulse: false,
  },
  monitor: {
    bg: 'bg-yellow-950/40',
    border: 'border-yellow-600/40',
    text: 'text-yellow-300',
    icon: Eye,
    pulse: false,
  },
  selfcare: {
    bg: 'bg-emerald-950/40',
    border: 'border-emerald-600/40',
    text: 'text-emerald-300',
    icon: Heart,
    pulse: false,
  },
};

export const TriageBadge: React.FC<TriageBadgeProps> = ({ level, language }) => {
  if (!level) return null;

  const t = TRANSLATIONS[language];
  const style = TRIAGE_STYLES[level];
  const Icon = style.icon;
  const label = t.triage[level];
  const action = t.triage[`${level}Action` as keyof typeof t.triage];

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border mb-4 ${style.bg} ${style.border}`}
    >
      <div className={`relative flex-shrink-0 mt-0.5 ${style.pulse ? 'animate-pulse' : ''}`}>
        <Icon className={`w-5 h-5 ${style.text}`} />
      </div>
      <div>
        <p className={`text-sm font-bold tracking-wide ${style.text}`}>{label}</p>
        <p className={`text-xs mt-0.5 opacity-80 ${style.text}`}>{action}</p>
      </div>
    </div>
  );
};
